import { inject, Injectable } from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import { ICtaForm } from '@campaign-form/campaign-form.interface';
import {
  IDeliverableConfigurationApi,
  TDeliverableForm,
} from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/deliverable-list.interface';
import { IExecutionForm } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/execution-step/execution-list/execution/execution.interface';
import { IExpectedKpiForm } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/expected-kpi-step/execution-expected-kpi-list/execution-expected-kpis/expected-kpi-list/expected-kpi/expected-kpi.interface';
import { EDeliverableType, EPlatformFeature } from '@enums/campaign.enum';
import { TExpectedKpi } from '@interfaces/campaign-analytics.interface';
import { TranslateService } from '@ngx-translate/core';
import { SocialUtilitiesService } from '@utilities/social-utilities.service';
import { camelCase } from 'lodash';
import { firstValueFrom } from 'rxjs';
import { ExpectedKpiListService } from './deliverable-flow-dialog/expected-kpi-step/execution-expected-kpi-list/execution-expected-kpis/expected-kpi-list/expected-kpi-list.service';

export interface IApplyPlatformChangeParams {
  ctaForm: FormGroup<ICtaForm>;
  deliverableConfigurations: IDeliverableConfigurationApi[];
  sourcePlatformFeature: EPlatformFeature;
  targetPlatformFeature: EPlatformFeature;
}

export interface IPlatformChangeImpact {
  willRemoveDeliverables: boolean;
  willRemoveExpectedKpis: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class CtaTemplatePlatformChangeService {
  private readonly deliverableTypeMap: Record<string, EDeliverableType> = {
    audioTrackings: EDeliverableType.audioTracking,
    captions: EDeliverableType.caption,
    chatbot: EDeliverableType.chatBot,
    chatbots: EDeliverableType.chatBot,
    contents: EDeliverableType.content,
    customs: EDeliverableType.custom,
    descriptions: EDeliverableType.description,
    gamePlays: EDeliverableType.gamePlay,
    hashTags: EDeliverableType.hashTag,
    mentions: EDeliverableType.mention,
    messages: EDeliverableType.message,
    productPlacements: EDeliverableType.productPlacement,
    showcases: EDeliverableType.showcase,
    talkingPoints: EDeliverableType.talkingPoint,
    textTrackings: EDeliverableType.textTracking,
    titles: EDeliverableType.title,
    visuals: EDeliverableType.visual,
  };

  private readonly expectedKpiListService = inject(ExpectedKpiListService);
  private readonly translate = inject(TranslateService);

  public async getPlatformChangeImpact(params: IApplyPlatformChangeParams): Promise<IPlatformChangeImpact> {
    const { ctaForm, deliverableConfigurations, targetPlatformFeature } = params;
    const availableDeliverableTypes: Set<EDeliverableType> = this.getAvailableDeliverableTypes(
      deliverableConfigurations,
      targetPlatformFeature,
    );
    const availableKpiSet: Set<TExpectedKpi> = await this.getAvailableKpiSet(
      ctaForm.controls.executions,
      targetPlatformFeature,
    );

    return {
      willRemoveDeliverables: this.willRemoveDeliverables(ctaForm.controls.deliverables, availableDeliverableTypes),
      willRemoveExpectedKpis: this.willRemoveExpectedKpis(ctaForm.controls.executions, availableKpiSet),
    };
  }

  public async applyPlatformChange(params: IApplyPlatformChangeParams): Promise<void> {
    const { ctaForm, deliverableConfigurations, sourcePlatformFeature, targetPlatformFeature } = params;
    const availableDeliverableTypes: Set<EDeliverableType> = this.getAvailableDeliverableTypes(
      deliverableConfigurations,
      targetPlatformFeature,
    );
    const availableKpiSet: Set<TExpectedKpi> = await this.getAvailableKpiSet(
      ctaForm.controls.executions,
      targetPlatformFeature,
    );

    this.removeUnavailableDeliverables(ctaForm.controls.deliverables, availableDeliverableTypes);
    this.normalizeExecutions(
      ctaForm.controls.executions,
      availableKpiSet,
      sourcePlatformFeature,
      targetPlatformFeature,
    );

    ctaForm.updateValueAndValidity();
  }

  private async getAvailableKpiSet(
    executionFormArray: FormArray<FormGroup<IExecutionForm>>,
    targetPlatformFeature: EPlatformFeature,
  ): Promise<Set<TExpectedKpi>> {
    if (!this.hasExpectedKpis(executionFormArray)) {
      return new Set<TExpectedKpi>();
    }

    const availableKpis: TExpectedKpi[] = await firstValueFrom(
      this.expectedKpiListService.getPlatformFeatureKpisGoal(targetPlatformFeature),
    );

    return new Set(availableKpis ?? []);
  }

  private getAvailableDeliverableTypes(
    deliverableConfigurations: IDeliverableConfigurationApi[],
    targetPlatformFeature: EPlatformFeature,
  ): Set<EDeliverableType> {
    return deliverableConfigurations.reduce((accumulator: Set<EDeliverableType>, configuration) => {
      if (camelCase(configuration.platformFeature) !== camelCase(targetPlatformFeature)) {
        return accumulator;
      }

      accumulator.add(this.getNormalizedDeliverableType(configuration.deliverable));

      return accumulator;
    }, new Set<EDeliverableType>());
  }

  private getNormalizedDeliverableType(deliverableType: string): EDeliverableType {
    const normalizedDeliverableType = camelCase(deliverableType);

    return this.deliverableTypeMap[normalizedDeliverableType] ?? (normalizedDeliverableType as EDeliverableType);
  }

  private normalizeExecutions(
    executionFormArray: FormArray<FormGroup<IExecutionForm>>,
    availableKpiSet: Set<TExpectedKpi>,
    sourcePlatformFeature: EPlatformFeature,
    targetPlatformFeature: EPlatformFeature,
  ): void {
    const executionName: string = this.translate.instant(`shared.platformFeatures.${targetPlatformFeature}`);
    const shouldClearDuration: boolean =
      !SocialUtilitiesService.isNonStreamPlatformFeature(sourcePlatformFeature) &&
      SocialUtilitiesService.isNonStreamPlatformFeature(targetPlatformFeature);

    executionFormArray.controls.forEach((executionControl: FormGroup<IExecutionForm>) => {
      this.removeUnavailableExpectedKpis(executionControl.controls.expectedKpis, availableKpiSet);
      executionControl.controls.name.setValue(executionName);

      if (shouldClearDuration) {
        executionControl.controls.duration.setValue(null);
      }
    });
  }

  private removeUnavailableDeliverables(
    deliverableFormArray: FormArray<FormGroup<TDeliverableForm>>,
    availableDeliverableTypes: Set<EDeliverableType>,
  ): void {
    for (let index: number = deliverableFormArray.length - 1; index >= 0; index -= 1) {
      const deliverableControl: FormGroup<TDeliverableForm> = deliverableFormArray.at(index);
      const deliverableType: EDeliverableType = deliverableControl.controls.type.value;

      if (availableDeliverableTypes.has(deliverableType)) {
        continue;
      }

      deliverableFormArray.removeAt(index);
    }
  }

  private willRemoveDeliverables(
    deliverableFormArray: FormArray<FormGroup<TDeliverableForm>>,
    availableDeliverableTypes: Set<EDeliverableType>,
  ): boolean {
    return deliverableFormArray.controls.some((deliverableControl: FormGroup<TDeliverableForm>) => {
      const deliverableType: EDeliverableType = deliverableControl.controls.type.value;

      return !availableDeliverableTypes.has(deliverableType);
    });
  }

  private removeUnavailableExpectedKpis(
    expectedKpiFormArray: FormArray<FormGroup<IExpectedKpiForm>>,
    availableKpiSet: Set<TExpectedKpi>,
  ): void {
    for (let index: number = expectedKpiFormArray.length - 1; index >= 0; index -= 1) {
      const expectedKpiControl: FormGroup<IExpectedKpiForm> = expectedKpiFormArray.at(index);
      const kpi: TExpectedKpi | null = expectedKpiControl.controls.kpi.value;

      if (kpi && availableKpiSet.has(kpi)) {
        continue;
      }

      expectedKpiFormArray.removeAt(index);
    }
  }

  private willRemoveExpectedKpis(
    executionFormArray: FormArray<FormGroup<IExecutionForm>>,
    availableKpiSet: Set<TExpectedKpi>,
  ): boolean {
    return executionFormArray.controls.some((executionControl: FormGroup<IExecutionForm>) => {
      return executionControl.controls.expectedKpis.controls.some((expectedKpiControl: FormGroup<IExpectedKpiForm>) => {
        const kpi: TExpectedKpi | null = expectedKpiControl.controls.kpi.value;

        return !!kpi && !availableKpiSet.has(kpi);
      });
    });
  }

  private hasExpectedKpis(executionFormArray: FormArray<FormGroup<IExecutionForm>>): boolean {
    return executionFormArray.controls.some((executionControl: FormGroup<IExecutionForm>) => {
      return executionControl.controls.expectedKpis.length > 0;
    });
  }
}
